import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import Footer from '../sections/Footer';
import Menu from '../sections/Menu';
import { getAuth, signOut, onAuthStateChanged } from 'firebase/auth';
import BlogCard from '../components/BlogCard';
import PageMeta from '../seo/PageMeta';
import { buildCollectionPageStructuredData } from '../seo/structuredData';
import {
  BLOG_CATEGORIES,
  BLOG_DEFAULT_DESCRIPTION,
  BLOG_HEADER_TITLE,
  getCategoryMetaByName,
  splitPostCategories,
} from '../blog/categories';
import { sortPostsByDateDesc } from '../blog/dates';

const allowedEmails = [
    'iwona.bendig@egodziny.pl',
    'krzysztof.warda@egodziny.pl',
    'dominik.pogorzelski@egodziny.pl'
  ];

const POSTS_PER_PAGE = 10;

function Blog() {
  const [posts, setPosts] = useState([]);
  const [isLoadingPosts, setIsLoadingPosts] = useState(true);
  const [user, setUser] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const categoryParam = searchParams.get('category') || 'all';
  const validCategoryIds = ['all', ...BLOG_CATEGORIES.map((c) => c.id)];
  const selectedCategoryId = validCategoryIds.includes(categoryParam) ? categoryParam : 'all';
  const currentPage = Math.max(1, parseInt(searchParams.get('page'), 10) || 1);

  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, currentUser => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, [auth]);

  useEffect(() => {
    async function fetchPosts() {
      setIsLoadingPosts(true);
      try {
        const querySnapshot = await getDocs(collection(db, 'blogs'));
        const postsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));

        const visiblePosts = postsData.filter(post =>
          post.public === true || (auth.currentUser && allowedEmails.includes(auth.currentUser.email))
        );

        setPosts(visiblePosts);
      } finally {
        setIsLoadingPosts(false);
      }
    }

    fetchPosts();
  }, [user, auth]);

  const handleLogout = () => {
    signOut(auth);
  };
  const canEditPosts = Boolean(user?.email && allowedEmails.includes(user.email));

  const activeCategory = useMemo(
    () => BLOG_CATEGORIES.find((category) => category.id === selectedCategoryId) || null,
    [selectedCategoryId]
  );

  const sortedPosts = useMemo(() => sortPostsByDateDesc(posts), [posts]);

  const filteredPosts = useMemo(() => {
    if (selectedCategoryId === 'all') {
      return sortedPosts;
    }

    return sortedPosts.filter((post) =>
      splitPostCategories(post.category).some((categoryName) => {
        const meta = getCategoryMetaByName(categoryName);
        return meta?.id === selectedCategoryId;
      })
    );
  }, [selectedCategoryId, sortedPosts]);

  const totalPages = Math.max(1, Math.ceil(filteredPosts.length / POSTS_PER_PAGE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginatedPosts = useMemo(() => {
    const start = (safeCurrentPage - 1) * POSTS_PER_PAGE;
    return filteredPosts.slice(start, start + POSTS_PER_PAGE);
  }, [filteredPosts, safeCurrentPage]);

  const updateParams = (nextCategoryId, nextPage) => {
    const params = {};
    if (nextCategoryId && nextCategoryId !== 'all') {
      params.category = nextCategoryId;
    }
    if (nextPage && nextPage > 1) {
      params.page = String(nextPage);
    }
    setSearchParams(params, { replace: false });
  };

  const setSelectedCategoryId = (nextCategoryId) => {
    updateParams(nextCategoryId, 1);
  };

  const goToPage = (page) => {
    const next = Math.min(Math.max(1, page), totalPages);
    updateParams(selectedCategoryId, next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const collectionStructuredData = useMemo(() => {
    const itemListElements = sortedPosts
      .filter((post) => post.public !== false)
      .map((post) => ({
      name: post.title,
      url: `/blog/${post.id}`,
      }));

    return [
      buildCollectionPageStructuredData({
        path: "/blog",
        name: "Blog eGodziny",
        description: BLOG_DEFAULT_DESCRIPTION,
        itemListElements,
      }),
    ];
  }, [sortedPosts]);

  const handleCategoryPickByName = (categoryName) => {
    const categoryMeta = getCategoryMetaByName(categoryName);
    if (categoryMeta) {
      setSelectedCategoryId(categoryMeta.id);
    }
  };

  const bannerStyle = {
    backgroundColor: activeCategory?.bgColor || 'rgba(64, 126, 249, 0.14)',
    borderLeft: `6px solid ${activeCategory?.color || '#407EF9'}`,
  };

  return (
    <>
      <PageMeta
        path="/blog"
        fallbackTitle={`${BLOG_HEADER_TITLE} | eGodziny`}
        fallbackDescription={BLOG_DEFAULT_DESCRIPTION}
        structuredData={collectionStructuredData}
      />
      <Menu />

      <div className="blog-main-page" style={{ padding: '2rem' }}>
        <div className='blog-banner' style={bannerStyle}>
          <h1 className='blog-title'>{BLOG_HEADER_TITLE}</h1>
          <p
            className='blog-category-context'
            style={{ color: activeCategory?.color || '#1D4ED8' }}
          >
            {activeCategory
              ? `Kategoria: ${activeCategory.name}`
              : 'Wszystkie kategorie'}
          </p>
          <p className='blog-description'>
            {activeCategory ? activeCategory.description : BLOG_DEFAULT_DESCRIPTION}
          </p>

          <div className='blog-category-filters'>
            <button
              type="button"
              className={`blog-category-chip ${selectedCategoryId === 'all' ? 'active' : ''}`}
              onClick={() => setSelectedCategoryId('all')}
            >
              Wszystkie
            </button>
            {BLOG_CATEGORIES.map((category) => (
              <button
                key={category.id}
                type="button"
                className={`blog-category-chip ${
                  selectedCategoryId === category.id ? 'active' : ''
                }`}
                style={{
                  color: category.color,
                  backgroundColor: category.bgColor,
                  borderColor: category.borderColor,
                }}
                onClick={() => setSelectedCategoryId(category.id)}
                title={category.name}
              >
                {category.label}
              </button>
            ))}
          </div>

          {user ? (
            <div className='blog-buttons'>
              <button className='login button-default btn' onClick={handleLogout}>
                Wyloguj
              </button>
              {canEditPosts && (
                <button
                  className='button-default btn'
                  onClick={() => navigate("/edit/new")}
                >
                  Dodaj nowy post
                </button>
              )}
            </div>
          ) : null}
        </div>

        <div className='blog-cards'>
          {isLoadingPosts ? (
            <div className="blog-loading-state" role="status" aria-live="polite">
              <span className="blog-spinner" aria-hidden="true" />
              <span className="blog-loading-text">Ładuję posty…</span>
            </div>
          ) : paginatedPosts.length > 0 ? (
            paginatedPosts.map(post => (
              <BlogCard
                key={post.id}
                post={post}
                onCategoryClick={handleCategoryPickByName}
                canEdit={canEditPosts}
              />
            ))
          ) : (
            <p className="blog-empty-state">
              Brak postów w tej kategorii.
            </p>
          )}

          {totalPages > 1 ? (
            <nav className="blog-pagination" aria-label="Stronicowanie">
              <button
                type="button"
                className="blog-pagination-arrow"
                onClick={() => goToPage(safeCurrentPage - 1)}
                disabled={safeCurrentPage === 1}
                aria-label="Poprzednia strona"
              >
                ‹
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  type="button"
                  className={`blog-pagination-page ${page === safeCurrentPage ? 'active' : ''}`}
                  onClick={() => goToPage(page)}
                  aria-current={page === safeCurrentPage ? 'page' : undefined}
                >
                  {page}
                </button>
              ))}
              <button
                type="button"
                className="blog-pagination-arrow"
                onClick={() => goToPage(safeCurrentPage + 1)}
                disabled={safeCurrentPage === totalPages}
                aria-label="Następna strona"
              >
                ›
              </button>
            </nav>
          ) : null}
        </div>

      </div>

      <Footer />
    </>
  );
}

export default Blog;
